import useCases from '@/hooks/useCases';
import { autoFocusedFieldRef } from '@/utils/forms';
import { convertCaseUrgencyToXMStatusType } from '@/utils/misc';
import { cloudCards } from '@/utils/theme';
import { ArrowForwardRounded, BoltRounded, Close, FactCheckRounded, RateReviewRounded, VerifiedRounded, WarningRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import { ChangeEvent, useState } from 'react';
import DuplicateCodesWarning from './DuplicateCodesWarning';
import { EAudioProcessingStep } from '@/types/audio/EAudioProcessingStep';

const GODKENDT_WARNING_KEY = 'skipGodkendtWarning';

interface IFlagDialogProps {
  open: boolean;
  onClose: () => void;
  hasDuplicateCodes?: boolean;
}

export const FlagDialog = observer(({ open, onClose, hasDuplicateCodes = false }: IFlagDialogProps) => {
  const cases = useCases();
  const [flagReason, setFlagReason] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [isAttest, setIsAttest] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showGodkendtWarning, setShowGodkendtWarning] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const flagAndClear = async (confirmedNote: boolean) => {
    const status = convertCaseUrgencyToXMStatusType(isUrgent);
    const confirmedNoteText = confirmedNote ? '[GODKENDT] ' : '';
    const isAttestText = isAttest ? '[ATTEST] ' : '';
    const flagReasonText = `${confirmedNoteText}${isAttestText}${flagReason}`;

    await cases.flagOrCompleteAndSubmitCaseToEHR(status, isUrgent, true, flagReasonText, confirmedNote, isAttest);

    transaction(() => {
      cases.clearCase();
      cases.disableForceRedirectToNewEhr();
      cases.enableForceRedirectToInbox();
    });
  };

  const handleSubmit = async (confirmedNote: boolean) => {
    cases.updateConfirmedNoteStatus(confirmedNote);
    setSubmitLoading(true);
    try {
      if (!cases.isNew() || !cases.hasRecordingBeenUsed()) {
        await flagAndClear(confirmedNote);
        handleClose();
        return;
      }

      await cases.waitForFullAudioAndProcessIt(async () => {
        await flagAndClear(confirmedNote);
        handleClose();
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCardClick = (confirmedNote: boolean) => {
    if (isLoading || showGodkendtWarning) return;
    if (confirmedNote && localStorage.getItem(GODKENDT_WARNING_KEY) !== 'true') {
      setShowGodkendtWarning(true);
      return;
    }
    handleSubmit(confirmedNote);
  };

  const handleConfirmGodkendt = () => {
    if (dontShowAgain) localStorage.setItem(GODKENDT_WARNING_KEY, 'true');
    setShowGodkendtWarning(false);
    handleSubmit(true);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setFlagReason(event.target.value);
  const handleToggleUrgency = (event: ChangeEvent<HTMLInputElement>) => setIsUrgent(event.target.checked);
  const handleToggleAttest = (event: ChangeEvent<HTMLInputElement>) => setIsAttest(event.target.checked);

  const handleClose = () => {
    if (isLoading) return;
    setFlagReason('');
    setIsUrgent(false);
    setIsAttest(false);
    setShowGodkendtWarning(false);
    setDontShowAgain(false);
    setSubmitLoading(false);
    onClose();
  };

  const isLoading =
    submitLoading ||
    cases.fullAudioProcessingStep === EAudioProcessingStep.STORING ||
    cases.segmentAudioProcessingStep === EAudioProcessingStep.STORING;

  const cards = [
    {
      confirmedNote: false,
      icon: <RateReviewRounded />,
      title: 'Send til gennemsyn',
      description: 'Lægesekretæren gennemgår og retter diktatet, samt sørger for kodning og opfølgning.',
      accent: '#4192EC',
      borderColor: 'rgba(65, 146, 236, 0.4)',
      hoverBorder: 'rgba(65, 146, 236, 0.8)',
      hoverBg: 'rgba(65, 146, 236, 0.04)',
      btnColor: 'secondary' as const,
      btnVariant: 'outlined' as const,
    },
    {
      confirmedNote: true,
      icon: <VerifiedRounded />,
      title: 'Godkend notat',
      description: 'Du godkender direkte. Diktatet gennemgås ikke — kun kodning og opfølgning.',
      accent: '#4CAF50',
      borderColor: 'rgba(76, 175, 80, 0.4)',
      hoverBorder: 'rgba(76, 175, 80, 0.8)',
      hoverBg: 'rgba(76, 175, 80, 0.04)',
      btnColor: 'success' as const,
      btnVariant: 'contained' as const,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isLoading}
      onClick={(e) => e.stopPropagation()}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer - 1,
        '.MuiDialog-paper': {
          ...cloudCards,
          width: '100%',
          maxWidth: '640px',
          borderRadius: '24px',
          overflow: 'hidden',
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Afslut notat
        </Typography>
        <IconButton onClick={handleClose} disabled={isLoading} size="small" className="dd-privacy-allow">
          <Close />
        </IconButton>
      </Stack>

      <DialogContent sx={{ px: 3, py: 0 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            inputRef={autoFocusedFieldRef}
            autoFocus
            value={flagReason}
            onChange={handleChange}
            autoComplete="off"
            margin="none"
            id="name"
            label="Bemærkning"
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Tilføj en bemærkning til lægesekretæren..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.5)',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                '&.Mui-focused': {
                  boxShadow: '0 0 0 3px rgba(65, 146, 236, 0.12)',
                },
              },
            }}
          />

          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  color="error"
                  checked={isUrgent}
                  onChange={handleToggleUrgency}
                />
              }
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <BoltRounded sx={{ fontSize: 18, color: isUrgent ? 'error.main' : 'text.secondary' }} />
                  <Typography component="span" variant="body2">
                    <strong>Hastende:</strong> marker som akut
                  </Typography>
                </Stack>
              }
              labelPlacement="start"
              sx={{
                m: 0,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: isUrgent ? 'error.main' : 'divider',
                bgcolor: isUrgent ? 'rgba(244, 67, 54, 0.04)' : 'transparent',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '& .MuiFormControlLabel-label': { flex: 1 },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  color="secondary"
                  checked={isAttest}
                  onChange={handleToggleAttest}
                />
              }
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <FactCheckRounded sx={{ fontSize: 18, color: isAttest ? 'secondary.main' : 'text.secondary' }} />
                  <Typography component="span" variant="body2">
                    <strong>Attest:</strong> marker til attest
                  </Typography>
                </Stack>
              }
              labelPlacement="start"
              sx={{
                m: 0,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: isAttest ? 'secondary.main' : 'divider',
                bgcolor: isAttest ? 'rgba(65, 146, 236, 0.04)' : 'transparent',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '& .MuiFormControlLabel-label': { flex: 1 },
              }}
            />
          </Stack>

          {hasDuplicateCodes && <DuplicateCodesWarning />}
        </Stack>
      </DialogContent>

      <Divider sx={{ mt: 2.5, mb: 0 }} />

      {showGodkendtWarning ? (
        <Stack spacing={2} sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <WarningRounded color="error" sx={{ fontSize: 24 }} />
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" fontWeight={700}>
                Er du sikker på, at du vil godkende notatet?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Når du godkender, vil diktatet ikke blive lyttet til, og der vil ikke blive foretaget
                rettelser. Lægesekretæren læser udelukkende notatet for kodning og opfølgning. Vær
                sikker på, at notatet er korrekt og fuldt udfyldt.
              </Typography>
            </Stack>
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                size="small"
              />
            }
            label="Vis ikke denne advarsel igen"
            sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}
          />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="text" color="primary" onClick={() => setShowGodkendtWarning(false)}>
              Tilbage
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmGodkendt}
              disabled={isLoading}
              className="dd-privacy-allow"
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <VerifiedRounded />}
            >
              Ja, godkend notatet
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1.5} sx={{ p: 3 }}>
          {cards.map((card) => (
            <Box
              key={card.title}
              role="button"
              tabIndex={isLoading ? -1 : 0}
              onClick={() => handleCardClick(card.confirmedNote)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCardClick(card.confirmedNote);
                }
              }}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: card.borderColor,
                bgcolor: 'rgba(255, 255, 255, 0.4)',
                cursor: isLoading ? 'default' : 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': isLoading
                  ? {}
                  : {
                      borderColor: card.hoverBorder,
                      bgcolor: card.hoverBg,
                    },
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ color: card.accent, display: 'flex' }}>{card.icon}</Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {card.title}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, minHeight: '3em' }}>
                  {card.description}
                </Typography>
                <Button
                  variant={card.btnVariant}
                  color={card.btnColor}
                  size="small"
                  disabled={isLoading}
                  className="dd-privacy-allow"
                  endIcon={<ArrowForwardRounded />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(card.confirmedNote);
                  }}
                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                >
                  {card.title}
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Dialog>
  );
});
